class PDPPopups extends HTMLElement {
  constructor() {
    super();
    this.scrollPosition = 0;
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const stickyAtc = document.querySelector("[data-lk-sticky]");

    const getPopupElement = (selector) => {
      if (!selector) return null;

      const bySelector = document.querySelector(selector);
      if (bySelector) return bySelector;

      if (selector.startsWith("#")) {
        return document.getElementById(selector.slice(1));
      }

      return null;
    };

    this.syncStickyAtcVisibility = () => {
      if (!stickyAtc) return;

      const hasOpenPopup = Array.from(document.querySelectorAll(".custom-popup")).some(
        (popup) => popup.style.display === "flex"
      );

      if (hasOpenPopup) {
        stickyAtc.dataset.popupSuppressed = "true";
        stickyAtc.style.display = "none";
        return;
      }

      delete stickyAtc.dataset.popupSuppressed;
      stickyAtc.style.display = "";
    };

    // === Open popup ===
    document.body.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-popup-target]");
      if (!trigger) return;
      e.preventDefault();

      const popupSelector = trigger.getAttribute("data-popup-target");
      const popup = getPopupElement(popupSelector);
      if (!popup) {
        console.warn("Popup not found:", popupSelector);
        return;
      }

      // Hide all popups first
      document.querySelectorAll(".custom-popup").forEach((p) => {
        p.style.display = "none";
      });

      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      
      // Show selected popup
      popup.style.display = "flex";
      this.syncStickyAtcVisibility();
      this.lockBodyScroll();

      // Mobile slide-up animation
      if (window.innerWidth <= 768) {
        const sheet = popup.querySelector(".custom-popup-content");
        if (sheet) {
          sheet.style.transform = 'translateY(100%)';
          setTimeout(() => {
            sheet.style.transition = 'transform 0.3s ease';
            sheet.style.transform = 'translateY(0)';
          }, 10);
        }
      }

      if (popup.id === "custom-popup-price_match") {
        this.loadHubspotPriceMatchForm();
      }
    });

    // === Mobile drag-to-close ===
    if (window.innerWidth <= 768) {
      this.initMobileDrag();
    }

    // === Close on overlay click ===
    document.body.addEventListener("click", (e) => {
      if (e.target.classList.contains("custom-popup")) {
        this.closePopup(e.target);
      }
    });

    // === Close via icons or back buttons ===
    document.body.addEventListener("click", (e) => {
      const btn = e.target.closest(".custom-close-icon, .custom-back-to-product");
      if (!btn) return;

      e.preventDefault();
      const popup = btn.closest(".custom-popup");
      if (popup) this.closePopup(popup);
    });

    // === Addon popup — Add to Cart ===
    document.body.addEventListener("click", async (e) => {
      const btn = e.target.closest(".addon-popup-atc");
      if (!btn) return;

      const variantId = Number(btn.dataset.variantId);
      if (!variantId) return;

      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = "Adding...";

      try {
        const res = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });

        if (!res.ok) throw new Error("Cart add failed");

        const popup = btn.closest(".custom-popup");
        if (popup) this.closePopup(popup);

        const drawer = document.querySelector("cart-drawer");
        if (drawer && typeof drawer.refreshCartDrawer === "function") {
          await drawer.refreshCartDrawer({ open: true });
        }
      } catch (err) {
        console.error("[AddonPopup] Add to cart failed:", err);
        btn.textContent = originalText;
        btn.disabled = false;
      }
    });
  }

  closePopup(popup) {
    if (window.innerWidth <= 768) {
      const sheet = popup.querySelector(".custom-popup-content");
      if (sheet) {
        sheet.style.transition = 'transform 0.3s ease';
        sheet.style.transform = 'translateY(100%)';
        setTimeout(() => {
          popup.style.display = "none";
          sheet.style.transform = '';
          sheet.style.transition = '';
          
          this.unlockBodyScroll();
          this.syncStickyAtcVisibility();
        }, 300);
        return;
      }
    }
    
    popup.style.display = "none";
    this.unlockBodyScroll();
    this.syncStickyAtcVisibility();
  }

  lockBodyScroll() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add("no-scroll_");
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  unlockBodyScroll() {
    document.body.classList.remove("no-scroll_");
    document.body.style.paddingRight = "";
  }

  initMobileDrag() {
    document.querySelectorAll(".custom-popup").forEach(popup => {
      const sheet = popup.querySelector(".custom-popup-content");
      if (!sheet) return;

      let startY = 0;
      let currentY = 0;
      let isDragging = false;

      const handleStart = (e) => {
        const initialScrollTop = sheet.scrollTop;
        const fromGrabber = Boolean(e.target.closest('.grabber'));
        if (fromGrabber || initialScrollTop <= 2) {
          isDragging = null;
          startY = e.touches[0].clientY;
        } else {
          startY = 0;
          isDragging = false;
        }
      };

      const handleMove = (e) => {
        if (startY === 0) return;
        
        currentY = e.touches[0].clientY;
        const diff = currentY - startY;

        if (diff < 0 && isDragging === null) {
          startY = 0;
          return;
        }

        if (diff > 5 && isDragging === null) {
          isDragging = true;
          sheet.style.transition = 'none';
        }

        if (isDragging === true && diff > 0) {
          e.preventDefault();
          sheet.style.transform = `translateY(${diff}px)`;
          const opacity = Math.max(0.3, 1 - (diff / 400));
          popup.style.backgroundColor = `rgba(0, 0, 0, ${opacity * 0.5})`;
        }
      };

      const handleEnd = () => {
        if (isDragging !== true) {
          startY = 0;
          isDragging = false;
          return;
        }
        
        const diff = currentY - startY;
        sheet.style.transition = 'transform 0.3s ease';
        
        if (diff > 150) {
          this.closePopup(popup);
        } else {
          sheet.style.transform = 'translateY(0)';
          popup.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
          setTimeout(() => {
            sheet.style.transition = '';
          }, 300);
        }
        
        startY = 0;
        isDragging = false;
      };

      sheet.addEventListener('touchstart', handleStart, { passive: false });
      sheet.addEventListener('touchmove', handleMove, { passive: false });
      sheet.addEventListener('touchend', handleEnd);
    });
  }

  loadHubspotPriceMatchForm() {
    const target = document.querySelector('#hubspot-price-match-form');
    if (!target) return;
    if (target.querySelector('iframe')) return;

    target.innerHTML = '';

    window.loadHsForms(function () {
      if (!(window.hbspt && window.hbspt.forms)) return;

      window.hbspt.forms.create({
        portalId: '21711334',
        formId: '87a8b02d-1766-4d58-9524-ba152fc63d22',
        target: '#hubspot-price-match-form',
      });
    });
  }
}

customElements.define("pdp-popups", PDPPopups);

if (document.readyState !== "loading") {
  document.querySelectorAll("pdp-popups").forEach((el) => el.connectedCallback());
} else {
  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("pdp-popups").forEach((el) => el.connectedCallback());
  });
}
